import { Form, Select } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import {
    setFilter,
    testRunState,
} from '../../../../../redux/testRun/testRunList.slice'
import {
    createFetchEpics,
    createFetchSprints,
    useAsyncSelect,
} from '../../../../../utils/asyncSelectHelpers'
import { isValidArray, isValidObject } from '../../../../../utils/helpers'
import { resolveTestRunProjectId } from '../../../../shared/TestRunTabs'
import AsyncTagField from '../../../../shared/AsyncTagField'
import styles from '../../../../../testrun.module.css'

const STATUS_OPTIONS = ['Pass','Pending', 'Retest','Fail']

//======================================================================================================

function FilterForm(props) {
    const {
        stateTestRun = {},
        setStateTestRun = () => {},
        // handleTestCaseList = () => {},
        handleClose = () => {},
        projectId: projectIdProp,
        getPopupContainer,
    } = props

    const [form] = Form.useForm()
    const dispatch = useDispatch()

    const { filter, defaultFilter } = useSelector(testRunState)

    const [submitType, setSubmitType] = useState('close')
    const [tagsOpen, setTagsOpen] = useState(false)

    const [saveLoading, setSaveLoading] = useState({
        saveAndNext: false,
        saveAndClose: false,
    })

    // hook up 3 async selects
    const projectId = resolveTestRunProjectId(projectIdProp)
    const fetchEpicsForProject = useMemo(
        () => createFetchEpics(projectId),
        [projectId]
    )
    const fetchSprintsForProject = useMemo(
        () => createFetchSprints(projectId),
        [projectId]
    )
    const epicSelect = useAsyncSelect(fetchEpicsForProject)
    const sprintSelect = useAsyncSelect(fetchSprintsForProject)
    const sprintSelectRef = useRef(null)
    // const tagSelect = useAsyncSelect(fetchTags)

    const blurSprintSelect = () => {
        requestAnimationFrame(() => {
            sprintSelectRef.current?.blur?.()
        })
    }

    const watchedStatus = Form.useWatch('status', form)
    const watchedSprint = Form.useWatch('sprint', form)
    const watchedTags = Form.useWatch('tags', form)

    const hasFilterSelected = useMemo(() => {
        const statusValue =
            watchedStatus?.value ?? watchedStatus ?? null
        const sprintValue =
            watchedSprint?.value ?? watchedSprint ?? null
        const tags = Array.isArray(watchedTags) ? watchedTags : []
        return Boolean(statusValue) || Boolean(sprintValue) || tags.length > 0
    }, [watchedStatus, watchedSprint, watchedTags])

    const handleSubmit = async (values) => {
        setSaveLoading({
            saveAndClose: submitType === 'close',
            saveAndNext: submitType === 'next',
        })

        const selectedEpic = epicSelect?.options?.filter((item) =>
            values?.epic?.includes(item?.value)
        )
        const selectedSprint = sprintSelect?.options?.find(
            (item) => item?.value === (values?.sprint?.value || values?.sprint)
        )

        dispatch(
            setFilter({
                epic: isValidObject(selectedEpic) ? selectedEpic : '',
                sprint: isValidObject(selectedSprint) ? selectedSprint : '',
                tags: isValidArray(values?.tags) ? values?.tags : [],
                status: values?.status?.value || values?.status,
            })
        )

        setTimeout(() => {
            setStateTestRun({
                ...stateTestRun,
                filterPopover: false,
            })
            setSaveLoading({
                saveAndClose: false,
                saveAndNext: false,
            })
        }, 500)
    }

    const handleClear = () => {
        form.setFieldsValue({
            status: null,
            sprint: null,
            tags: [],
        })
        dispatch(setFilter(defaultFilter))
        // setStateTestRun({
        //     ...stateTestRun,
        //     filterPopover: false,
        // })
    }

    useEffect(() => {
        form.setFieldsValue({
            // epic: filter?.epic || [],
            sprint: filter?.sprint || null,
            tags: filter?.tags || [],
            status: filter?.status || null,
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateTestRun, filter])

   /*  const getOptionsForMulti = (option) => {
        if (isValidArray(option)) {
            const finalData = option?.map((item) => {
                return {
                    label: item?.name || '',
                    value: item?._id || '',
                }
            })
            return finalData
        }
        return []
    } */

    return (
        <div className={styles.formPanel}>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    sprint: filter?.sprint ? filter?.sprint : null,
                    epic: filter?.epic || [],
                    tags: filter?.tags || [],
                    status: filter?.status || null,
                }}
            >
                <Form.Item
                    label="Status"
                    name="status"
                    placeholder="Please select status"
                    rules={[
                        {
                            required: false,
                            message: 'Please select a Status',
                        },
                    ]}
                    style={{ marginBottom: 16 }}
                >
                    <Select
                        allowClear
                        showSearch
                        autoComplete="off"
                        placeholder="Select Status"
                        optionFilterProp="label"
                        getPopupContainer={getPopupContainer}
                        suffixIcon={
                            <DownOutlined
                                style={{ fontSize: 11, color: '#64748b' }}
                            />
                        }
                        options={STATUS_OPTIONS.map((s) => ({
                            value: s,
                            label: s,
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    label="Sprint"
                    name="sprint"
                    rules={[
                        {
                            required: false,
                            message: 'Please select a Sprint',
                        },
                    ]}
                    style={{ marginBottom: 16 }}
                >
                    <Select
                        ref={sprintSelectRef}
                        allowClear
                        showSearch
                        autoComplete="off"
                        placeholder="Search & select Sprint"
                        filterOption={false}
                        searchValue={sprintSelect.searchValue}
                        autoClearSearchValue={false}
                        getPopupContainer={getPopupContainer}
                        suffixIcon={
                            <DownOutlined
                                style={{ fontSize: 11, color: '#64748b' }}
                            />
                        }
                        onSearch={sprintSelect.onSearch}
                        onOpenChange={(open) => {
                            sprintSelect.onDropdownVisibleChange(open)
                            if (open) setTagsOpen(false)
                            if (!open) blurSprintSelect()
                        }}
                        onSelect={() => {
                            sprintSelect.onSelect()
                            setTagsOpen(false)
                            blurSprintSelect()
                        }}
                        onChange={() => {
                            sprintSelect.onSelect()
                            setTagsOpen(false)
                            blurSprintSelect()
                        }}
                        onClear={sprintSelect.onClear}
                        onPopupScroll={sprintSelect.onPopupScroll}
                        options={sprintSelect.options}
                        loading={sprintSelect.loading}
                        notFoundContent={sprintSelect.notFoundContent}
                        optionLabelProp="label"
                    />
                </Form.Item>

                <AsyncTagField
                    form={form}
                    projectId={projectId}
                    creatable={false}
                    maxTagCount={5}
                    selectClassName="pool-tag-select"
                    open={tagsOpen}
                    onOpenChange={setTagsOpen}
                    getPopupContainer={getPopupContainer}
                    rules={[
                        {
                            required: false,
                            message: 'Please choose at least one item',
                        },
                    ]}
                />

                {/* Sticky actions */}
                <div className={styles.formActions}>
                    <button
                        type="submit"
                        className={`${styles.submitButton} ${
                            saveLoading?.saveAndClose || !hasFilterSelected
                                ? styles.submitButtonDisabled
                                : ''
                        }`}
                        disabled={
                            saveLoading?.saveAndClose || !hasFilterSelected
                        }
                        onClick={() => setSubmitType('close')}
                    >
                        {saveLoading?.saveAndClose ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleClear}
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        className={styles.cancelButton}
                        onClick={handleClose}
                    >
                        Cancel
                    </button>
                </div>
            </Form>
        </div>
    )
}

export default FilterForm
